import {inject, injectable} from 'inversify';
import {makeAutoObservable, runInAction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {AnswerModel} from '../../../../core/domain/Answer/Answer.model';
import type {QuestionModel} from '../../../../core/domain/Question/Question.model';
import type {Nullable} from '../../../../utils/utility-types';
import type {CromModel} from '../domain/Crom/Crom.model';

import InfoSVG from '../../../../assets/icons/primary/Info Circle.svg';
import {alert} from '../../../../components/alert';
import {TextAnswerModel} from '../../../../core/domain/Answer/TextAnswer/TextAnswer.model';
import {MissedCromsCountStore} from '../../../../core/stores/MissedCromsCount.store';
import {COMMAND} from '../../../../services/entity-action/utils';
import {AsyncBusyManager, type WithBusyManager} from '../../../../utils/store';
import {FetchCromByIdUseCase} from '../useCases/FetchCromById.uc';
import {SendCromAnswersUseCase} from '../useCases/SendCromAnswers.uc';

type BusyKey = 'fetch' | 'sendAnswers';

@injectable()
export class CromSolvingStore implements WithBusyManager<BusyKey> {
  busy = new AsyncBusyManager<BusyKey>();
  questionWithErrorIds: Set<string> = new Set();
  private answersByQuestionId = new Map<string, AnswerModel>();
  private crom: Nullable<CromModel> = null;
  private questionsById = new Map<string, QuestionModel>();

  constructor(
    @inject(FetchCromByIdUseCase)
    private readonly fetchCromByIdUseCase: FetchCromByIdUseCase,
    @inject(SendCromAnswersUseCase)
    private readonly sendCromAnswersUseCase: SendCromAnswersUseCase,
    @inject(MissedCromsCountStore)
    private readonly cromsCountStore: MissedCromsCountStore,
  ) {
    this.busy.observe(this, {
      fetch: 'fetch',
    });

    this.busy.register('sendAnswers');

    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  get clientName() {
    return this.crom?.client.name ?? i18n.t('shared:client_not_found');
  }

  get hasSelectedOptions() {
    return Array.from(this.answersByQuestionId.values()).some(
      answer => answer.isValid,
    );
  }

  get isAiGenerated(): boolean {
    return this.crom?.isAiGenerated ?? false;
  }

  get isFetching() {
    return this.busy.getReason('fetch');
  }

  get isSendingAnswers() {
    return this.busy.getReason('sendAnswers');
  }

  get questions() {
    return Array.from(this.questionsById.values());
  }

  get validationMessage() {
    return this.questionWithErrorIds.size > 0
      ? i18n.t('shared:answer_required_questions')
      : '';
  }

  async fetch(cromId: string) {
    try {
      const crom = await this.fetchCromByIdUseCase.execute(cromId);

      runInAction(() => {
        this.crom = crom;
        this.questionsById = new Map(
          crom.questions.map(question => [question.id, question]),
        );

        if (crom.answers) {
          this.answersByQuestionId = new Map(crom.answers);
        }
      });
    } catch (e) {
      alert.error(e);
    }
  }

  getAnswerByQuestionId(questionId: string) {
    return this.answersByQuestionId.get(questionId);
  }

  async sendAnswers() {
    if (this.crom?.id) {
      try {
        this.busy.setReason('sendAnswers', true);

        const areAllQuestionsAnswered =
          this.crom.questions.length === this.answersByQuestionId.size;

        if (!this.validate()) {
          return COMMAND.CANCEL;
        }

        if (!areAllQuestionsAnswered) {
          const confirmed = await this.showQuestionsWithoutAnswersConfirm();

          if (!confirmed) {
            return COMMAND.CANCEL;
          }
        }

        await this.sendCromAnswersUseCase.execute({
          answersByQuestionId: this.answersByQuestionId,
          cromId: this.crom.id,
        });

        this.cromsCountStore.decrementCount(this.crom.client.id);
      } catch (e) {
        alert.error(e);

        return COMMAND.CANCEL;
      } finally {
        this.busy.setReason('sendAnswers', false);
      }
    }
  }

  setAnswer(answer: AnswerModel) {
    if (answer instanceof TextAnswerModel) {
      this.answersByQuestionId.set(answer.question.id, answer);
    } else if (answer.value) {
      this.answersByQuestionId.set(answer.question.id, answer);
    } else {
      this.answersByQuestionId.delete(answer.question.id);
    }
  }

  private showQuestionsWithoutAnswersConfirm() {
    return alert.confirm({
      buttons: [i18n.t('shared:yes_submit'), i18n.t('shared:no')],
      icon: InfoSVG,
      iconColor: 'icon3',
      isDismissible: true,
      message: i18n.t('shared:you_left_questions_without_answers'),
      title: i18n.t('shared:are_you_sure'),
    });
  }

  private validate() {
    this.questionWithErrorIds.clear();

    this.questionsById.forEach((question, questionId) => {
      const isValid =
        this.answersByQuestionId.get(questionId)?.isValid ?? false;

      if (question.isRequired && !isValid) {
        this.questionWithErrorIds.add(questionId);
      }
    });

    return this.questionWithErrorIds.size === 0;
  }
}
